import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Course } from 'src/@types/Course';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// client-side-pagination-list-pages: server-side pagination + search for the
// admin Courses grid. getCourseList fetches one page from GET /courses/list
// ({total,page,pageSize,items}); the component drives refetch on page/search.
const PAGE_SIZE = 12;

export const useCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterRole] = useState('all');
  const [currentCourse, setCurrentCourse] = useState<Course | undefined>();
  const [page, setPage] = useState(1); // 1-based (MUI Pagination)
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getCourseList = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(APIS.COURSES.PAGINATED, {
        params: { page, pageSize: PAGE_SIZE, search: filterName || undefined },
      });
      const data = response.data?.data ?? response.data;
      setCourses(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load courses', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterName = (value: string) => {
    setFilterName(value);
    setPage(1); // reset to first page on a new search
  };

  const handleEditRow = (selected: Course) => {
    setCurrentCourse(selected);
    setModalOpen(true);
  };
  const handleDeleteRow = async (id: string) => {
    try {
      await axiosInstance.delete(APIS.COURSES.PATCH(id));
      await axiosInstance.post(APIS.SUPPORT.handleDelete, {
        course: id,
        type: 'COURSE',
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    }
    setConfirmationOpen(true);
    getCourseList();
  };

  return {
    modalOpen,
    setModalOpen,
    confirmationOpen,
    setConfirmationOpen,
    filterName,
    filterRole,
    currentCourse,
    setCurrentCourse,
    filteredData: courses,
    page,
    setPage,
    total,
    pageSize: PAGE_SIZE,
    loading,
    handleDeleteRow,
    handleEditRow,
    handleFilterName,
    getCourseList,
  };
};
